/**
 * Utility functions for file operations
 */

export type FileType =
  | 'excel'
  | 'pdf'
  | 'csv'
  | 'json'
  | 'text'
  | 'xml'
  | 'word'
  | 'image'
  | 'unknown';

export interface FileMetadata {
  type: FileType;
  extension: string;
  icon: string;
  label: string;
  canPreview: boolean;
}

/**
 * Get file extension from filename
 */
export function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Determine file type from file name and MIME type
 */
export function getFileType(fileName: string, mimeType: string): FileType {
  const extension = getFileExtension(fileName);

  // Check by extension first
  if (['xlsx', 'xls'].includes(extension)) return 'excel';
  if (extension === 'pdf') return 'pdf';
  if (extension === 'csv') return 'csv';
  if (extension === 'json') return 'json';
  if (['txt', 'text'].includes(extension)) return 'text';
  if (extension === 'xml') return 'xml';
  if (['doc', 'docx'].includes(extension)) return 'word';
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';

  // Check by MIME type
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'excel';
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('csv') || mimeType === 'text/csv') return 'csv';
  if (mimeType === 'application/json') return 'json';
  if (mimeType.startsWith('text/')) return 'text';
  if (mimeType.includes('xml')) return 'xml';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'word';
  if (mimeType.startsWith('image/')) return 'image';

  return 'unknown';
}

/**
 * Get file metadata including icon and label
 */
export function getFileMetadata(fileName: string, mimeType: string): FileMetadata {
  const type = getFileType(fileName, mimeType);
  const extension = getFileExtension(fileName);

  const metadataMap: Record<FileType, Omit<FileMetadata, 'type' | 'extension'>> = {
    excel: {
      icon: '📊',
      label: 'Excel',
      canPreview: true, // Excel can be previewed with SheetJS
    },
    pdf: {
      icon: '📄',
      label: 'PDF',
      canPreview: true, // PDF can be previewed with object URL
    },
    csv: {
      icon: '📋',
      label: 'CSV',
      canPreview: true,
    },
    json: {
      icon: '📝',
      label: 'JSON',
      canPreview: true,
    },
    text: {
      icon: '📄',
      label: 'Text',
      canPreview: true,
    },
    xml: {
      icon: '📄',
      label: 'XML',
      canPreview: true,
    },
    word: {
      icon: '📝',
      label: 'Word',
      canPreview: true, // Word can be previewed with mammoth.js for .docx
    },
    image: {
      icon: '🖼️',
      label: 'Image',
      canPreview: true,
    },
    unknown: {
      icon: '📄',
      label: extension.toUpperCase() || 'File',
      canPreview: false,
    },
  };

  return {
    type,
    extension,
    ...metadataMap[type],
  };
}

/**
 * Check if file can be previewed in browser
 */
export function canPreviewFile(file: File): boolean {
  const metadata = getFileMetadata(file.name, file.type);

  if (metadata.canPreview) {
    return true;
  }

  // Check if it's a text-based file by MIME type
  if (
    file.type.startsWith('text/') ||
    file.type === 'application/json' ||
    file.name.endsWith('.csv') ||
    file.name.endsWith('.json') ||
    file.name.endsWith('.txt') ||
    file.name.endsWith('.xml')
  ) {
    return true;
  }

  // Check for image files
  if (
    file.type.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(getFileExtension(file.name))
  ) {
    return true;
  }

  // Check for PDF
  if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
    return true;
  }

  // Check for Excel
  if (
    file.type.includes('excel') ||
    file.type.includes('spreadsheet') ||
    ['xlsx', 'xls'].includes(getFileExtension(file.name))
  ) {
    return true;
  }

  // Check for Word
  if (
    file.type.includes('word') ||
    file.type.includes('document') ||
    ['doc', 'docx'].includes(getFileExtension(file.name))
  ) {
    return true;
  }

  return false;
}

/**
 * Generate unique file ID
 */
export function generateFileId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

/**
 * Format file size to human-readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Validate file size against maximum
 */
export function validateFileSize(file: File, maxSize?: number): { valid: boolean; error?: string } {
  if (!maxSize) {
    return { valid: true };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size exceeds maximum allowed size of ${formatFileSize(maxSize)}`,
    };
  }

  return { valid: true };
}

/**
 * Check if file type is allowed
 */
export function isFileTypeAllowed(file: File, allowedTypes?: string[]): boolean {
  if (!allowedTypes || allowedTypes.length === 0) {
    return true;
  }

  const extension = getFileExtension(file.name);
  const mimeType = file.type;

  return allowedTypes.some((allowed) => {
    // Check extension
    if (allowed.startsWith('.')) {
      return extension === allowed.substring(1);
    }
    // Check MIME type
    if (allowed.includes('/')) {
      return mimeType === allowed || mimeType.startsWith(allowed.split('/')[0] + '/');
    }
    // Check file type name
    const fileType = getFileType(file.name, mimeType);
    return fileType === allowed.toLowerCase();
  });
}
